import { useCallback, useEffect, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { Download, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Panel } from "@/components/extranet/StatusBadge";
import { http, idr, errMsg, fmtDate } from "@/lib/api";
import GroupCommission from "@/components/extranet/GroupCommission";

const thisMonth = () => new Date().toISOString().slice(0, 7);

const Card = ({ label, value, testid, strong }) => (
  <div className="rounded-md border border-[#E7E0D6] bg-white p-4" data-testid={testid}>
    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
    <p className={`mt-1 text-xl font-extrabold ${strong ? "text-[#1F5F8B]" : "text-slate-900"}`}>{value}</p>
  </div>
);

export default function CommissionReport() {
  const { property, select } = useOutletContext();
  const [sp, setSp] = useSearchParams();
  const month = sp.get("month") || thisMonth();
  const basis = sp.get("basis") || "check_out";
  const view = sp.get("view") || "property";
  const upd = (k, v) => { const n = new URLSearchParams(sp); n.set(k, v); setSp(n, { replace: true }); };
  const setMonth = (v) => upd("month", v);
  const setBasis = (v) => upd("basis", v);
  const [data, setData] = useState(null);
  const [dl, setDl] = useState(null);
  const setDownload = useCallback((d) => setDl(d), []);
  const openProperty = (id) => { const n = new URLSearchParams(sp); n.set("view", "property"); setSp(n, { replace: true }); select(id); };

  useEffect(() => {
    if (view !== "property") return;
    setData(null);
    setDl({ url: `/extranet/properties/${property.id}/commission-report`, name: `komisi-${property.slug || property.id}-${month}.csv` });
    http.get(`/extranet/properties/${property.id}/commission-report`, { params: { month, basis } })
      .then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [property.id, property.slug, month, basis, view]);

  const download = async () => {
    if (!dl) return;
    try {
      const r = await http.get(dl.url, { params: { month, basis, format: "csv" }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = dl.name; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const s = data?.summary;
  return (
    <div className="fade-up flex flex-col gap-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl">Laporan Komisi</h1>
          <p className="mt-1 text-sm text-slate-500">Booking langsung (Booking Engine) yang terkonfirmasi. Komisi sesuai persentase saat booking dibuat.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input data-testid="report-month" type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="w-40 bg-white" />
          <Select value={basis} onValueChange={setBasis}>
            <SelectTrigger data-testid="report-basis" className="w-56 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="check_out">Per tanggal check-out</SelectItem>
              <SelectItem value="created">Per tanggal booking</SelectItem>
            </SelectContent>
          </Select>
          <button data-testid="report-download" onClick={download} className="flex items-center gap-1.5 rounded-md bg-[#006CE4] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0052B4]"><Download className="h-4 w-4" /> Unduh CSV</button>
          {view === "property" && <Link data-testid="report-invoice" to={`/extranet/invoice/${property.id}/${month}`} className="flex items-center gap-1.5 rounded-md border border-[#006CE4] bg-white px-4 py-2 text-sm font-semibold text-[#006CE4] hover:bg-blue-50"><FileText className="h-4 w-4" /> Invoice</Link>}
        </div>
      </div>
      <div className="flex gap-2">
        {[["property", `Per properti · ${property.name}`], ["group", "Semua properti (grup)"]].map(([k, l]) => (
          <button key={k} data-testid={`report-view-${k}`} onClick={() => upd("view", k)} className={`rounded-md px-3 py-1.5 text-sm font-semibold ${view === k ? "bg-[#1F5F8B] text-white" : "bg-white text-slate-600 ring-1 ring-slate-200"}`}>{l}</button>
        ))}
      </div>
      {view === "group" ? <GroupCommission month={month} basis={basis} setDownload={setDownload} onOpen={openProperty} /> : <>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Card label="Total booking" value={s ? s.bookings : "…"} testid="report-total-bookings" />
        <Card label="Malam kamar" value={s ? s.room_nights : "…"} testid="report-room-nights" />
        <Card label="Harga kamar" value={s ? idr(s.room_revenue) : "…"} testid="report-room-revenue" />
        <Card label="Komisi terutang" value={s ? idr(s.commission) : "…"} testid="report-commission" strong />
        <Card label="Net ke hotel" value={s ? idr(s.net) : "…"} testid="report-net" />
      </div>
      <Panel title={`Detail booking · ${fmtDate(`${month}-01`, "MMMM yyyy")}`} testid="report-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC] text-left text-xs uppercase text-slate-500">
              <tr><th className="px-5 py-2.5">Kode</th><th>Tamu</th><th>Kamar</th><th>Check-in</th><th>Check-out</th><th className="text-right">Harga kamar</th><th className="text-right">Total</th><th className="text-right">Komisi</th><th className="pr-5 text-right">Net</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data && data.rows.length === 0 && <tr><td colSpan={9} data-testid="report-empty" className="px-5 py-10 text-center text-slate-400">Tidak ada booking di bulan ini</td></tr>}
              {data?.rows.map((r) => (
                <tr key={r.code} data-testid={`report-row-${r.code}`}>
                  <td className="px-5 py-3 font-mono-x text-xs text-[#006CE4]">{r.code}</td><td className="font-medium">{r.guest}</td><td>{r.room}</td>
                  <td>{fmtDate(r.check_in, "d MMM")}</td><td>{fmtDate(r.check_out, "d MMM")}</td>
                  <td className="text-right">{idr(r.room_revenue)}</td><td className="text-right">{idr(r.grand_total)}</td>
                  <td className="text-right">{idr(r.commission)} <span className="text-xs text-slate-500">({r.commission_percent}%)</span></td>
                  <td className="pr-5 text-right font-semibold">{idr(r.net)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
      </>}
    </div>
  );
}
